/**
 * Scrub podkladového videa v heru – pozice scrollu uvnitř sekce mapuje čas videa.
 */
(() => {
    const media = document.querySelector<HTMLElement>('[data-hero-media]')
    const video = document.querySelector<HTMLVideoElement>('[data-hero-video]')
    const hero = media?.closest<HTMLElement>('[data-hero]')
    if (!media || !video || !hero) return

    // Kam nejdál smí dojít úvodní přehrání, pokud ho nepřeruší scroll.
    const INTRO_UNTIL = .5

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let target = 0
    let frame = 0
    let intro = true
    // Místo předání videa scrollu – snímek, na kterém úvod skončil, a proscrollovaná
    // část hera v tu chvíli. Zbytek klipu se rozprostře do zbytku výšky sekce, takže
    // předání nikdy neškubne a čím dřív návštěvník scrollne, tím větší kus scrubuje.
    let scrubFrom = 0
    let anchor = 0
    let lastScrollY = window.scrollY

    /**
     * Skok na poslední známý cíl.
     */
    const seek = (): void => {
        // Během úvodu si video řídí čas samo, cíl je ještě nedopočítaný.
        if (intro) return
        // Další seek až po dokončení předchozího – každé přiřazení currentTime
        // ruší rozpracovaný seek, takže při plynulém scrollu by se nestihl
        // dekódovat žádný snímek a video by přeskočilo až po zastavení.
        if (video.seeking) return
        if (Math.abs(video.currentTime - target) < 0.01) return

        video.currentTime = target
    }

    const onFrame = (): void => {
        frame = 0
        seek()
    }

    /**
     * Proscrollovaná část hera. Hero je první sekce, ale začíná pod lepivou
     * hlavičkou – od horní hrany viewportu by scrub prospal výšku hlavičky
     * scrollu, dokud sekce nevyjede zpod hlavičky. Posun stránky je tady
     * zároveň posunem hera.
     */
    const scrolled = (): number => {
        // Video je fixní, zůstává vidět dokud hero úplně neodjede – scrub tedy
        // běží přes celou výšku sekce, ne jen po dosažení spodní hrany viewportu.
        const range = hero.offsetHeight
        if (range <= 0) return 0

        // Safari při přetažení nahoře vrací negativní scrollY, dolní mez ho drží.
        return Math.min(Math.max(window.scrollY / range, 0), 1)
    }

    const update = (): void => {
        const rect = hero.getBoundingClientRect()
        // Čte se až tady – metadata se můžou načíst dřív, než tenhle skript naběhne.
        const duration = video.duration

        media.classList.toggle('is-past', rect.bottom <= 0)

        if (intro || !Number.isFinite(duration)) return

        const remaining = 1 - anchor
        const progress = remaining > 0 ? Math.min((scrolled() - anchor) / remaining, 1) : 1

        target = (scrubFrom + Math.max(progress, 0) * (1 - scrubFrom)) * duration
        frame ||= requestAnimationFrame(onFrame)
    }

    /**
     * Předání videa scrollu – po doběhnutí úvodu nebo hned při prvním scrollu.
     */
    const endIntro = (): void => {
        if (!intro) return

        intro = false
        video.pause()

        if (Number.isFinite(video.duration)) {
            scrubFrom = video.currentTime / video.duration
            anchor = scrolled()
        }

        update()
    }

    /**
     * Hlídání konce úvodu; `timeupdate` chodí po čtvrtsekundách a přejel by ho.
     */
    const watchIntro = (): void => {
        if (!intro) return
        if (video.paused || video.currentTime >= video.duration * INTRO_UNTIL) return endIntro()

        requestAnimationFrame(watchIntro)
    }

    /**
     * Zamítnuté přehrání (Safari v režimu nízké spotřeby, zákaz auto-play pro web)
     * nechá Safari bez jediného vykresleného snímku – vynucený seek ho dokreslí.
     */
    const onPlayDenied = (): void => {
        endIntro()
        video.currentTime = target
    }

    const startIntro = (): void => {
        // Obnovená pozice scrollu už patří scrubu, úvod by z ní ucukl.
        if (!intro || reducedMotion || hero.getBoundingClientRect().top < 0) return endIntro()

        video.play().then(watchIntro, onPlayDenied)
    }

    /**
     * Scroll má přednost před úvodem – hned si video přebírá. Událost bez posunu
     * (Safari ji umí vystřelit při načtení) úvod ukončit nesmí.
     */
    const onScroll = (): void => {
        if (window.scrollY === lastScrollY) return

        lastScrollY = window.scrollY
        endIntro()
        update()
    }

    const onMetadata = (): void => {
        update()
        startIntro()
    }

    video.addEventListener('seeked', seek)
    video.addEventListener('loadedmetadata', onMetadata)
    video.addEventListener('ended', endIntro)

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', update)

    // Safari vyhodnocuje `media` na <source> už při parsování – dokud se načítá
    // styl, nezná rozměr viewportu, oba zdroje zahodí a na nový už nečeká.
    // Opakovaný výběr proběhne až teď, s hotovým layoutem.
    const source = video.querySelector('source')
    if (!video.currentSrc && source && window.matchMedia(source.media).matches) {
        video.load()
    }

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
        onMetadata()
    } else {
        update()
    }
})();
